/**
 * Chunk Error Handler
 * Layer 4: ChunkLoadError fallback with retry mechanism
 *
 * Handles dynamic import failures (ChunkLoadError) that occur when:
 * - Old chunks are requested after a new deployment
 * - Network errors prevent chunk loading
 * - CDN/cache issues
 *
 * Strategy:
 * 1. Detect ChunkLoadError
 * 2. Try once to reload the page (clears cache)
 * 3. If still fails, show error to user
 */

import { useUpdateStore } from '../stores/updateStore';

const CHUNK_RETRY_KEY = 'chunk_load_retry_timestamp';
const RETRY_COOLDOWN = 10000; // 10 seconds

interface ChunkErrorDetails {
  error: Error;
  timestamp: number;
  url?: string;
  retryAttempt: number;
}

class ChunkErrorHandler {
  private errorLog: ChunkErrorDetails[] = [];
  private hasReloaded: boolean = false;

  constructor() {
    this.setupErrorListener();
    this.checkPreviousRetry();
  }

  /**
   * Setup global error listener for ChunkLoadError
   */
  private setupErrorListener(): void {
    window.addEventListener('error', (event) => {
      this.handleError(event.error, event);
    });

    // Also listen for unhandled promise rejections (for dynamic imports)
    window.addEventListener('unhandledrejection', (event) => {
      this.handleError(event.reason, event);
    });
  }

  /**
   * Check if we just reloaded due to a chunk error
   */
  private checkPreviousRetry(): void {
    const retryTimestamp = sessionStorage.getItem(CHUNK_RETRY_KEY);

    if (retryTimestamp) {
      const timestamp = parseInt(retryTimestamp, 10);
      const elapsed = Date.now() - timestamp;

      if (elapsed < RETRY_COOLDOWN) {
        this.hasReloaded = true;
      }

      // Clear the retry flag
      sessionStorage.removeItem(CHUNK_RETRY_KEY);
    }
  }

  /**
   * Handle potential chunk loading errors
   */
  private handleError(error: any, event?: Event | PromiseRejectionEvent): void {
    if (!this.isChunkError(error)) {
      return;
    }

    // Silence the bubble — chunk errors are expected after a deploy and the
    // user-visible UpdateNotification toast handles the recovery path.
    if (event && 'preventDefault' in event) {
      event.preventDefault();
    }
    if (event && 'stopImmediatePropagation' in event && typeof (event as any).stopImmediatePropagation === 'function') {
      (event as any).stopImmediatePropagation();
    }

    // Keep an internal log without noisy console.error output.
    this.logError(error);

    // If we already tried reloading, surface via the toast instead of looping.
    if (this.hasReloaded) {
      this.showErrorToUser(error);
      return;
    }

    // Attempt to reload the page (one shot per session).
    this.reloadPage();
  }

  /**
   * Check if error is a chunk loading error
   */
  private isChunkError(error: any): boolean {
    if (!error) return false;

    const errorMessage = error.message || error.toString();

    // Common chunk error patterns
    const chunkErrorPatterns = [
      'ChunkLoadError',
      'Loading chunk',
      'failed to fetch',
      'dynamically imported module',
      'Failed to fetch dynamically imported module',
      'Importing a module script failed',
      'error loading dynamically imported module'
    ];

    return chunkErrorPatterns.some(pattern =>
      errorMessage.toLowerCase().includes(pattern.toLowerCase())
    );
  }

  /**
   * Log error details
   */
  private logError(error: Error): void {
    const details: ChunkErrorDetails = {
      error,
      timestamp: Date.now(),
      url: window.location.href,
      retryAttempt: this.hasReloaded ? 1 : 0
    };

    this.errorLog.push(details);

    // Keep only last 10 errors
    if (this.errorLog.length > 10) {
      this.errorLog.shift();
    }
  }

  /**
   * Reload the page to fetch new chunks
   */
  private reloadPage(): void {
    console.log('[ChunkErrorHandler] Reloading page to fetch new chunks...');

    // Mark that we're reloading due to chunk error
    sessionStorage.setItem(CHUNK_RETRY_KEY, Date.now().toString());

    // Hard reload (bypass cache)
    window.location.reload();
  }

  /**
   * Show error message to user via the global update-notification toast.
   * Force-set the update-required flag so the toast shows immediately,
   * even if version polling hasn't fired yet.
   */
  private showErrorToUser(error: Error): void {
    window.dispatchEvent(new CustomEvent('app:chunk-error', {
      detail: {
        error,
        message: 'Failed to load application resources. Please refresh the page.',
        canRetry: false
      }
    }));

    // Surface via the global UpdateNotification toast (decoupled from omnibar).
    try {
      useUpdateStore.getState().setUpdateRequired(true);
    } catch (e) {
      console.warn('[ChunkErrorHandler] Failed to set updateRequired:', e);
    }
  }

  /**
   * Get error log (for debugging)
   */
  getErrorLog(): ChunkErrorDetails[] {
    return [...this.errorLog];
  }

  /**
   * Clear error log
   */
  clearErrorLog(): void {
    this.errorLog = [];
  }

  /**
   * Check if page was reloaded due to chunk error
   */
  wasReloaded(): boolean {
    return this.hasReloaded;
  }
}

// Singleton instance
export const chunkErrorHandler = new ChunkErrorHandler();

/**
 * Public helper — detect chunk-load errors from outside the handler
 * (e.g. React error boundaries) so they can be silenced uniformly.
 */
export function isChunkLoadError(error: any): boolean {
  if (!error) return false;
  const errorMessage = error.message || error.toString();
  const patterns = [
    'ChunkLoadError',
    'Loading chunk',
    'failed to fetch',
    'dynamically imported module',
    'Failed to fetch dynamically imported module',
    'Importing a module script failed',
    'error loading dynamically imported module',
  ];
  return patterns.some((p) => errorMessage.toLowerCase().includes(p.toLowerCase()));
}

/**
 * Wrapper for dynamic imports with retry logic
 * Usage: const Component = await retryImport(() => import('./Component'))
 */
export async function retryImport<T>(
  importFn: () => Promise<T>,
  retries: number = 1,
  delay: number = 1000
): Promise<T> {
  try {
    return await importFn();
  } catch (error) {
    if (retries > 0 && chunkErrorHandler['isChunkError'](error)) {
      console.warn(`[retryImport] Retry import after ${delay}ms (${retries} retries left)`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryImport(importFn, retries - 1, delay * 2);
    }
    throw error;
  }
}

export default chunkErrorHandler;
